// Icons come from Lucide (one consistent 24px stroke set). This file keeps the app's names, so pages
// never import Lucide directly, and marks directional icons with `flip` so they mirror in Arabic.
import {
  ArrowDownUp,
  BadgeCheck,
  Bell,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  Flag,
  Globe,
  Heart,
  House,
  Info,
  Landmark,
  Mail,
  MapPin,
  MessageCircle,
  Minus,
  Package,
  Plus,
  ScanText,
  Search,
  Send,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Tag,
  TrendingDown,
  Truck,
  User,
  X,
  Zap,
  Phone,
  Receipt,
  Rocket,
  Eye,
  CalendarClock,
  Handshake,
  ListChecks,
  BellRing,
  Share2,
  type LucideIcon,
} from "lucide-react";

type P = { size?: number; className?: string; title?: string };

const make = (Icon: LucideIcon, directional = false) =>
  function AppIcon({ size = 22, className, title }: P) {
    return (
      <Icon
        size={size}
        strokeWidth={1.9}
        className={[directional ? "flip" : "", className ?? ""].join(" ").trim() || undefined}
        aria-hidden={title ? undefined : true}
        role={title ? "img" : undefined}
        aria-label={title}
      />
    );
  };

export const IconSearch = make(Search);
export const IconTag = make(Tag);
export const IconChat = make(MessageCircle);
export const IconBox = make(Package);
export const IconUser = make(User);
export const IconCart = make(ShoppingBag);
export const IconBack = make(ChevronLeft, true);
export const IconNext = make(ChevronRight, true);
export const IconCamera = make(Camera);
export const IconCheck = make(Check);
export const IconAlert = make(CircleAlert);
export const IconInfo = make(Info);
export const IconX = make(X);
export const IconTruck = make(Truck);
export const IconStore = make(Store);
export const IconScan = make(ScanText);
export const IconShield = make(ShieldCheck);
export const IconFlag = make(Flag);
export const IconPlus = make(Plus);
export const IconMinus = make(Minus);
export const IconBell = make(Bell);
export const IconGlobe = make(Globe);
export const IconBolt = make(Zap);
export const IconSend = make(Send, true);
export const IconFilter = make(SlidersHorizontal);
export const IconSort = make(ArrowDownUp);
export const IconPhone = make(Smartphone);
export const IconPin = make(MapPin);
export const IconMail = make(Mail);
export const IconBank = make(Landmark);
export const IconStar = make(Star);
export const IconVerified = make(BadgeCheck);
export const IconSparkle = make(Sparkles);
export const IconDown = make(TrendingDown);
export const IconChevronUp = make(ChevronUp);
export const IconChevronDown = make(ChevronDown);
export const IconHome = make(House);
export const IconCall = make(Phone);
export const IconReceipt = make(Receipt);
export const IconRocket = make(Rocket);
export const IconEye = make(Eye);
export const IconCalendar = make(CalendarClock);
export const IconDeal = make(Handshake);
export const IconChecklist = make(ListChecks);
export const IconAlertOn = make(BellRing);
export const IconShare = make(Share2);

/** WhatsApp's speech-bubble mark, drawn in the same stroke style as the rest of the set. */
export function IconWhatsApp({ size = 22, className }: P) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M3.5 20.5l1.3-4.1A8.5 8.5 0 1 1 8 19.6z" />
      <path d="M9 8.6c0 3.4 2.9 6.3 6.3 6.4l1.2-1.4-1.8-1-1 .8a4.2 4.2 0 0 1-2.4-2.4l.8-1-1-1.8z" />
    </svg>
  );
}

export const IconHeart = ({ filled, size = 22, className }: P & { filled?: boolean }) => (
  <Heart size={size} strokeWidth={1.9} className={className} fill={filled ? "currentColor" : "none"} aria-hidden />
);
